import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import BackMethods from "../../back_component/BackMethods";

function ModalHeaderPro({ title, onHide }) {
  return (
    <div className="users-admin-modal__header">
      <div className="users-admin-modal__header-main">
        <h5 className="users-admin-modal__title mb-0">{title}</h5>
      </div>
      <button type="button" className="btn-close" onClick={onHide} aria-label="إغلاق" />
    </div>
  );
}

function InlineField({ label, htmlFor, children }) {
  return (
    <div className="users-admin-modal__inline-field">
      <Form.Label htmlFor={htmlFor} className="users-admin-modal__inline-label">
        {label}
      </Form.Label>
      <div className="users-admin-modal__inline-control">{children}</div>
    </div>
  );
}

function PermissionsPicker({ permissions, selectedIds, onToggle, disabled }) {
  return (
    <div className="roles-admin-modal__permissions">
      {permissions.length === 0 ? (
        <div className="text-center py-3 text-muted small">جارٍ تحميل الصلاحيات...</div>
      ) : (
        permissions.map((perm) => (
          <Form.Check
            key={perm.permissionId}
            className="roles-admin-modal__perm-check"
            type="checkbox"
            id={`perm-${perm.permissionId}`}
            label={perm.displayName}
            checked={selectedIds.includes(perm.permissionId)}
            onChange={() => onToggle(perm.permissionId)}
            disabled={disabled}
          />
        ))
      )}
    </div>
  );
}

function CreateRoleModal({ show, onHide, onSaved }) {
  const { backEnd_request, UserTypes } = BackMethods();
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState({
    name: "",
    displayName: "",
    description: "",
    userType: 0,
    permissionIds: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [isLoadingPerms, setIsLoadingPerms] = useState(false);

  useEffect(() => {
    if (!show) return;
    setRole({
      name: "",
      displayName: "",
      description: "",
      userType: 0,
      permissionIds: [],
    });
    setFormError("");
    setIsSubmitting(false);
    setIsLoadingPerms(true);

    let cancelled = false;
    (async () => {
      try {
        const res = await backEnd_request.get("/admin/Permissions");
        if (cancelled) return;
        setPermissions(res.data || []);
      } catch {
        if (!cancelled) setPermissions([]);
      } finally {
        if (!cancelled) setIsLoadingPerms(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, backEnd_request]);

  const handleChange = (field, value) => {
    setRole((prev) => ({
      ...prev,
      [field]: field === "userType" ? Number(value) || 0 : value,
    }));
  };

  const togglePermission = (permissionId) => {
    setRole((prev) => {
      const exists = prev.permissionIds.includes(permissionId);
      return {
        ...prev,
        permissionIds: exists
          ? prev.permissionIds.filter((id) => id !== permissionId)
          : [...prev.permissionIds, permissionId],
      };
    });
  };

  const validate = () => {
    if (!role.name.trim()) return "الرجاء إدخال الاسم";
    if (!role.displayName.trim()) return "الرجاء إدخال الاسم الظاهر";
    if (role.userType === 0) return "الرجاء اختيار نوع المستخدم";
    if (role.permissionIds.length === 0) return "الرجاء اختيار صلاحية واحدة على الأقل";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }

    setIsSubmitting(true);
    try {
      await backEnd_request.post("/admin/Roles", role);
      onSaved?.({ message: "تمت إضافة الدور بنجاح.", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "حدث خطأ أثناء الإرسال");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="إضافة دور" onHide={onHide} />
      <Modal.Body>
        <p className="users-admin-modal__hint">
          <i className="bi bi-person-plus"></i>
          <span>أدخل بيانات الدور واختر الصلاحيات المطلوبة. يمكنك تعديلها لاحقاً.</span>
        </p>

        <Form onSubmit={handleSubmit}>
          <Row className="g-2 gx-lg-3">
            <Col lg={6}>
              <InlineField label="الاسم" htmlFor="role-name">
                <Form.Control
                  size="sm"
                  className="rounded-2"
                  id="role-name"
                  value={role.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </InlineField>
              <InlineField label="نوع المستخدم" htmlFor="role-userType">
                <Form.Select
                  size="sm"
                  className="rounded-2"
                  id="role-userType"
                  value={role.userType ? String(role.userType) : ""}
                  onChange={(e) => handleChange("userType", e.target.value === "" ? 0 : parseInt(e.target.value, 10))}
                >
                  <option value="">—</option>
                  {UserTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </InlineField>
            </Col>

            <Col lg={6}>
              <InlineField label="الاسم الظاهر" htmlFor="role-displayName">
                <Form.Control
                  size="sm"
                  className="rounded-2"
                  id="role-displayName"
                  value={role.displayName}
                  onChange={(e) => handleChange("displayName", e.target.value)}
                />
              </InlineField>
              <InlineField label="الوصف" htmlFor="role-description">
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  className="rounded-2"
                  id="role-description"
                  value={role.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                />
              </InlineField>
            </Col>
          </Row>

          <div className="roles-admin-modal__section-label mt-2 mb-2">
            <i className="bi bi-key" />
            <span>الصلاحيات</span>
          </div>

          <PermissionsPicker
            permissions={permissions}
            selectedIds={role.permissionIds}
            onToggle={togglePermission}
            disabled={isLoadingPerms}
          />

          {formError ? (
            <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">
              {formError}
            </div>
          ) : null}

          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> إضافة
                </>
              ) : (
                "إضافة الدور"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

function EditRoleModal({ show, roleId, onHide, onSaved }) {
  const { backEnd_request } = BackMethods();
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState({
    displayName: "",
    description: "",
    permissionIds: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show || !roleId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setFormError("");
        const [roleRes, permsRes] = await Promise.all([
          backEnd_request.get(`/admin/Roles/${roleId}`),
          backEnd_request.get("/admin/Permissions"),
        ]);
        if (cancelled) return;
        const roleData = roleRes.data || roleRes || {};
        const permsData = permsRes.data || permsRes || [];
        setRole({
          displayName: roleData.displayName || "",
          description: roleData.description || "",
          permissionIds: (roleData.permissions || []).map((p) => p.permissionId),
        });
        setPermissions(permsData);
      } catch (err) {
        if (!cancelled) {
          setFormError(err.response?.data?.message || "تعذر تحميل بيانات الدور");
          setPermissions([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, roleId, backEnd_request]);

  const togglePermission = (permissionId) => {
    setRole((prev) => {
      const exists = prev.permissionIds.includes(permissionId);
      return {
        ...prev,
        permissionIds: exists ? prev.permissionIds.filter((id) => id !== permissionId) : [...prev.permissionIds, permissionId],
      };
    });
  };

  const validate = () => {
    if (!role.displayName.trim()) return "الرجاء إدخال الاسم المعروض";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        displayName: role.displayName,
        description: role.description,
        permissionIds: role.permissionIds,
      };
      await backEnd_request.post(`/admin/Roles/${roleId}`, payload);
      onSaved?.({ message: "تم تعديل الدور بنجاح.", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "تعذر حفظ التعديلات");
    } finally {
      setIsSubmitting(false);
    }
  };

  const chip = useMemo(() => role.displayName, [role.displayName]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="تعديل دور" onHide={onHide} />
      <Modal.Body>
        {chip ? (
          <div className="users-admin-modal__user-chip mb-2 py-1">
            <i className="bi bi-person-badge text-secondary small" />
            <span className="text-muted" style={{ fontSize: "0.75rem" }}>
              الدور
            </span>
            <strong className="small">{chip}</strong>
          </div>
        ) : null}

        <Form onSubmit={handleSubmit}>
          <Row className="g-2 gx-lg-3">
            <Col lg={12}>
              {isLoading ? <div className="text-center py-2"><Spinner animation="border" size="sm" /></div> : null}
              <InlineField label="الاسم الظاهر" htmlFor="role-edit-displayName">
                <Form.Control
                  size="sm"
                  className="rounded-2"
                  id="role-edit-displayName"
                  value={role.displayName || ""}
                  onChange={(e) => setRole((prev) => ({ ...prev, displayName: e.target.value }))}
                  disabled={isLoading}
                />
              </InlineField>
              <InlineField label="الوصف" htmlFor="role-edit-description">
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  className="rounded-2"
                  id="role-edit-description"
                  value={role.description || ""}
                  onChange={(e) => setRole((prev) => ({ ...prev, description: e.target.value }))}
                  disabled={isLoading}
                />
              </InlineField>
            </Col>
          </Row>

          <div className="roles-admin-modal__section-label mt-2 mb-2">
            <i className="bi bi-key" />
            <span>الصلاحيات</span>
          </div>

          <PermissionsPicker
            permissions={permissions}
            selectedIds={role.permissionIds}
            onToggle={togglePermission}
            disabled={isLoading}
          />

          {formError ? (
            <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">
              {formError}
            </div>
          ) : null}

          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> حفظ
                </>
              ) : (
                "حفظ التعديلات"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

function RoleDetailsModal({ show, roleId, onHide }) {
  const { backEnd_request } = BackMethods();
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!show || !roleId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const [roleRes, permsRes] = await Promise.all([
          backEnd_request.get(`/admin/Roles/${roleId}`),
          backEnd_request.get("/admin/Permissions"),
        ]);
        if (cancelled) return;
        const roleData = roleRes.data || roleRes || {};
        const permsData = permsRes.data || permsRes || [];
        setRole(roleData);
        setPermissions(permsData);
      } catch {
        if (!cancelled) {
          setRole(null);
          setPermissions([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, roleId, backEnd_request]);

  const selectedIds = useMemo(() => {
    if (!role?.permissions?.length) return [];
    return role.permissions.map((p) => p.permissionId);
  }, [role]);

  const selectedPermissions = useMemo(() => {
    if (!permissions.length) return [];
    if (!selectedIds.length) return [];
    return permissions.filter((p) => selectedIds.includes(p.permissionId));
  }, [permissions, selectedIds]);

  const descriptionValue = useMemo(() => {
    if (!role) return "";
    return (
      role.description ??
      role.roleDescription ??
      role.desc ??
      role.Description ??
      role.about ??
      role.details ??
      ""
    );
  }, [role]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="معلومات الدور" onHide={onHide} />
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-2">
            <Spinner animation="border" size="sm" />
          </div>
        ) : role ? (
          <>
            <p className="users-admin-modal__hint" style={{ marginBottom: "0.6rem" }}>
              <i className="bi bi-info-circle" />
              <span>
                {role.displayName || "-"} ({role.name || "-"})
              </span>
            </p>

            <Row className="g-2 gx-lg-3">
              <Col lg={6}>
                <InlineField label="الاسم الظاهر" htmlFor="details-displayName">
                  <div className="roles-admin-modal__readonly-value">{role.displayName || "-"}</div>
                </InlineField>
                <InlineField label="مستخدم نظام" htmlFor="details-system">
                  <div className="roles-admin-modal__readonly-value">
                    {role.isSystemRole ? "نظام" : "غير نظام"}
                  </div>
                </InlineField>
              </Col>
              <Col lg={6}>
                <InlineField label="الوصف" htmlFor="details-description">
                  <div className="roles-admin-modal__readonly-value roles-admin-modal__readonly-value--desc">
                    {descriptionValue?.trim ? (descriptionValue.trim() || "-") : (descriptionValue || "-")}
                  </div>
                </InlineField>
              </Col>
            </Row>

            <div className="roles-admin-modal__section-label mt-3 mb-2">
              <i className="bi bi-key" />
              <span>الصلاحيات</span>
            </div>

            {selectedPermissions.length ? (
              <div className="roles-admin-modal__tags-grid">
                {selectedPermissions.map((p) => (
                  <span key={p.permissionId} className="roles-admin-modal__tag">
                    {p.displayName || p.name || p.permissionId}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-muted small">لا توجد صلاحيات لهذا الدور.</div>
            )}

            <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
              <Button variant="primary" size="sm" type="button" onClick={onHide}>
                تم
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center text-muted py-2 small">تعذر تحميل معلومات الدور</div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function RolesManagementModals({ modal, roleId, onClose, onSaved }) {
  return (
    <>
      <CreateRoleModal show={modal === "create"} onHide={onClose} onSaved={onSaved} />
      <EditRoleModal show={modal === "edit"} roleId={roleId} onHide={onClose} onSaved={onSaved} />
      <RoleDetailsModal show={modal === "details"} roleId={roleId} onHide={onClose} />
    </>
  );
}

